import { memo, useMemo } from 'react'
import { useLocation, useRoutes } from 'react-router-dom'

import { renderRouteElement } from '@/config/mainRoutes'
import { matchAppRoute } from '@/config/routeManifest'
import { pathnameOf } from '@/lib/shellSearch'
import type { WorkspaceTab } from '@/stores/app'

// One route tree per open tab, each rendered at ITS OWN location.
// `src/config/routeHostSpike.test.tsx` pins the react-router semantics this
// relies on: useRoutes(routes, suppliedLocation) installs a LocationContext
// for the supplied location, so useLocation/useParams/relative links inside a
// tab read the tab's location, not the live URL.
//
// HAZARD: navigation fired from inside a supplied-location tree still moves
// the REAL router — a background tab that navigates yanks the user. Worse, the
// shell hooks (useAppNavigate, useAppTabs) read the location they are given,
// and a supplied location has its SHELL KEYS stripped, so such a navigation
// would also drop the chat context (agent, conversationId, view).
//
// The invariant that keeps this safe: a background tab can never navigate. It
// is either unmounted (the default) or `<Activity mode="hidden">`, whose
// effects are unmounted and whose subtree cannot be clicked. Any retention
// change that breaks that invariant must first re-derive these hooks from the
// REAL router location instead of the supplied one.

interface AppRouteHostProps {
  tab: WorkspaceTab
  isActive: boolean
}

export const AppRouteHost = memo(function AppRouteHost({
  tab,
  isActive,
}: AppRouteHostProps) {
  const liveLocation = useLocation()
  // The active tab renders at the real location so nav state keeps driving its
  // element; an inactive tab is frozen at its stored href and has no state.
  // useRoutes parses the href string and fills the location defaults itself.
  const hrefPathname = useMemo(() => pathnameOf(tab.href), [tab.href])
  const pathname = isActive ? liveLocation.pathname : hrefPathname
  const state = isActive ? liveLocation.state : null

  // Manifest order and reservedParams (the /evals blocklist) are not
  // expressible in react-router's ranking, so the match comes from the
  // manifest and useRoutes only renders the single entry it picked.
  // matchAppRoute's cache keeps `matched` identity-stable, so this memo bails
  // for a background tab on every live navigation. It guards the element BUILD
  // only — LocationContext consumers inside every open tab still re-render on
  // navigation (react-router rebuilds the context value); same as before.
  // matched.params is passed as a prop BECAUSE useParams() inside the tree is
  // percent-DECODED and merged with the app shell's '/*' splat — divergent
  // from tab identity. Do not switch descendants to useParams for ids.
  const matched = matchAppRoute(pathname)
  const routes = useMemo(
    () =>
      matched
        ? [
            {
              path: matched.route.path,
              element: renderRouteElement(
                matched.route.routeId,
                matched.params,
                state
              ),
            },
          ]
        : [],
    [matched, state]
  )

  return useRoutes(routes, isActive ? liveLocation : tab.href)
})
